/* global window, document */

import React, { useEffect, useRef, useState } from 'react'
import Helmet from 'react-helmet'
import PropTypes from 'prop-types'

import { withPrefix } from 'gatsby'
import PageContext from '@dfb/gatsby-dfb-theme/src/components/page-context'
import { getBrand } from '@dfb/gatsby-dfb-theme/src/configuration/brands'
import { useLocation } from '@reach/router'
import WebComponentHeader from '../../../components/modules/web_component_header'
import WebComponentFooter from '../../../components/modules/web_component_footer'
import WebComponentChatbot from '../../../components/modules/web_component_chatbot'
import { getAdPageType } from '../../../utils/ads'
import { useUser } from '../../../utils/closedArea/UserContext'
import { sha256hex } from '../helper/hash'
import { interceptNewsletterLinks } from './utils/newsletter-modal'

const AmplitudeConsentLoader = ({ id, usercentricsName }) => {
  const [consent, setConsent] = useState(false)

  useEffect(() => {
    const handler = (e) => {
      const customEvent = e
      if (customEvent.detail && customEvent.detail.event === 'consent_status') {
        setConsent(Boolean(customEvent.detail[usercentricsName]))
      }
    }

    window.addEventListener('ucEvent', handler)

    return () => {
      window.removeEventListener('ucEvent', handler)
    }
  }, [usercentricsName])

  const { user, loading } = useUser() || {}
  const amplitudeUserIdSet = useRef(false)

  useEffect(() => {
    if (loading) return

    if (!user?.sub) {
      if (amplitudeUserIdSet.current) {
        amplitudeUserIdSet.current = false
        window.__pendingAmplitudeUserId = null
        if (window.amplitude) {
          window.amplitude.reset()
        }
      }
      return
    }

    let cancelled = false

    sha256hex(user.sub).then((hashedUid) => {
      if (cancelled) return

      amplitudeUserIdSet.current = true

      if (window.amplitude) {
        window.amplitude.setUserId(hashedUid)
      } else {
        window.__pendingAmplitudeUserId = hashedUid
      }
    })

    return () => {
      cancelled = true
    }
  }, [user, loading])

  useEffect(() => {
    if (!consent || !id) return
    if (window.__amplitudeInitialized) return

    const existingScript = document.querySelector('script[data-amplitude-sdk="true"]')

    const initAmplitude = () => {
      if (!window.amplitude) return
      if (window.__amplitudeInitialized) return

      window.amplitude.init(id, {
        fetchRemoteConfig: true,
        serverZone: 'EU',
        autocapture: true,
      })

      window.__amplitudeInitialized = true

      if (window.__pendingAmplitudeUserId) {
        window.amplitude.setUserId(window.__pendingAmplitudeUserId)
        window.__pendingAmplitudeUserId = null
      }

      if (window.__amplitudePendingEvents) {
        window.__amplitudePendingEvents.forEach(({ name, data }) => {
          window.amplitude.track(name, data)
        })
        window.__amplitudePendingEvents = []
      }
    }

    if (existingScript) {
      if (window.amplitude) {
        initAmplitude()
      } else {
        existingScript.addEventListener('load', initAmplitude, { once: true })
      }
      return
    }

    const script = document.createElement('script')
    script.src = `https://cdn.eu.amplitude.com/script/${id}.js`
    script.async = true
    script.dataset.amplitudeSdk = 'true'
    script.onload = initAmplitude

    document.head.appendChild(script)

    return () => {
      script.onload = null
    }
  }, [consent, id])

  return null
}

const Layout = ({ children, translations, pageUid, brand, language }) => {
  const { pathname } = useLocation()
  const brandBundleName = getBrand(brand).bundle

  useEffect(() => {
    const waitForConsent = (callback) => {
      // eslint-disable-next-line no-undef
      if (typeof window.__tcfapi !== 'function') {
        setTimeout(() => waitForConsent(callback), 100)
        return
      }

      // eslint-disable-next-line no-undef
      window.__tcfapi('addEventListener', 2, function (tcData, success) {
        if (!success) return

        if (tcData.eventStatus === 'useractioncomplete' || tcData.eventStatus === 'tcloaded') {
          callback()
        }
      })
    }

    waitForConsent(() => {
      // eslint-disable-next-line no-undef
      window.dataLayer = window.dataLayer || []
      // eslint-disable-next-line no-undef
      window['dataLayer'].push({ 'DFB.de_Pagetype': getAdPageType(pathname) })
      ;(function (w, d, s, l, i) {
        w[l] = w[l] || []
        w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
        var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l !== 'dataLayer' ? '&l=' + l : ''
        j.async = true
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
        f.parentNode.insertBefore(j, f)
        // eslint-disable-next-line no-undef
      })(window, document, 'script', 'dataLayer', 'GTM-5KPHHTJ6')
    })
  }, [pathname])

  useEffect(() => {
    if (process.env.USER_CENTRICS_CMP_ID) {
      // eslint-disable-next-line no-undef
      const usercentricsStub = document.createElement('script')
      usercentricsStub.src = 'https://web.eu1.cmp.usercentrics.eu/tcf/stub.js'
      // eslint-disable-next-line no-undef
      document.body.appendChild(usercentricsStub)

      // eslint-disable-next-line no-undef
      const usercentricsCmp = document.createElement('script')
      usercentricsCmp.id = 'usercentrics-cmp'
      usercentricsCmp.src = 'https://web.eu1.cmp.usercentrics.eu/ui/loader.js'
      usercentricsCmp.setAttribute('data-settings-id', process.env.USER_CENTRICS_CMP_ID)
      usercentricsCmp.setAttribute('data-language', language)
      usercentricsCmp.setAttribute('data-eu-mode', 'true')
      usercentricsCmp.async = true
      // eslint-disable-next-line no-undef
      document.body.appendChild(usercentricsCmp)
    }
  }, [language])

  useEffect(() => interceptNewsletterLinks('a[href="#newsletter"]', process.env.CDC_NEWSLETTER_API_KEY || ''), [])

  const chatbotRootSlugs = process.env.CHATBOT_ROOT_SLUGS?.split(',')
  const [showChatbot, setShowChatbot] = useState(false)
  useEffect(() => setShowChatbot(!!chatbotRootSlugs?.find((slug) => pathname.startsWith(slug))), [pathname, chatbotRootSlugs])

  return (
    <>
      {process.env.AMPLITUDE_TRACKING_ID && <AmplitudeConsentLoader id={process.env.AMPLITUDE_TRACKING_ID} usercentricsName="Amplitude" />}
      <Helmet htmlAttributes={{ class: 'u-SmoothScroll' }}>
        <script type="text/javascript" id="dfb-ui-modules-gatsby-context">
          window.gatsby_context = true
        </script>
        <script
          id="ui-kit-modules-brand-js-bundle"
          type="text/javascript"
          src={withPrefix(`/static/brands/${brandBundleName}/assets/main.bundle.js`)}
          onload="window.upgradeAllBehaviours()"
        />
        <link
          id="ui-kit-modules-brand-css-bundle"
          type="text/css"
          rel="stylesheet"
          href={withPrefix(`/static/brands/${brandBundleName}/assets/main.bundle.css`)}
        />
        {process.env.EYE_ABLE_CONFIG_URL && <script async src={process.env.EYE_ABLE_CONFIG_URL}></script>}
        {process.env.EYE_ABLE_CONFIG_URL && <script async src="https://cdn.eye-able.com/public/js/eyeAble.js"></script>}
        {process.env.DATACENTER_HOST && <meta name="turbo-root" content={`${process.env.DATACENTER_HOST}`} />}
        {process.env.GOOGLE_CONVERSION_ID && (
          <script type="text/plain" data-usercentrics="Google AdServices">{`
              /* <![CDATA[ */
              var google_conversion_id = ${process.env.GOOGLE_CONVERSION_ID};
              var google_custom_params = window.google_tag_params;
              var google_remarketing_only = true;
              /* ]]> */
          `}</script>
        )}
        {process.env.GOOGLE_CONVERSION_ID && (
          <script type="text/plain" data-usercentrics="Google AdServices" src="//www.googleadservices.com/pagead/conversion.js" />
        )}
        {process.env.META_PIXEL_ID && (
          <script type="text/plain" data-usercentrics="Facebook Pixel">{`
            !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function()
              {n.callMethod? n.callMethod.apply(n,arguments):n.queue.push(arguments)}
              ;if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${process.env.META_PIXEL_ID}');
              fbq('track', 'PageView');
          `}</script>
        )}
        {process.env.LINKEDIN_PARTNER_ID && (
          <script type="text/plain" data-usercentrics="LinkedIn Analytics">{`
            _linkedin_partner_id = "${process.env.LINKEDIN_PARTNER_ID}";
            window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
            window._linkedin_data_partner_ids.push(_linkedin_partner_id);
          `}</script>
        )}
        {process.env.LINKEDIN_PARTNER_ID && (
          <script type="text/plain" data-usercentrics="LinkedIn Analytics">{`
            (function(l) {
              if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
              window.lintrk.q=[]}
              var s = document.getElementsByTagName("script")[0];
              var b = document.createElement("script");
              b.type = "text/javascript";b.async = true;
              b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
              s.parentNode.insertBefore(b, s);})(window.lintrk);
          `}</script>
        )}
        {process.env.TIKTOK_ID && (
          <script type="text/plain" data-usercentrics="TikTok">{`
            !function (w, d, t) { w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
              ttq.load('${process.env.TIKTOK_ID}');
              ttq.page();
            }(window, document, 'ttq');
          `}</script>
        )}
      </Helmet>

      <PageContext.Provider value={{ pageUid: pageUid, translations: translations, brand }}>
        <WebComponentHeader brand={brand} language={language} />
        {children}
        <WebComponentFooter brand={brand} language={language} />

        {showChatbot && (
          <WebComponentChatbot
            name={process.env.CHATBOT_NAME}
            endpoint={process.env.CHATBOT_API_ENDPOINT}
            historySize={process.env.CHATBOT_API_HISTORY_SIZE}
          />
        )}
      </PageContext.Provider>
      <div id="alerts" className="m-Alerts"></div>
      <div className="u-Sprite" data-href={withPrefix(`/static/brands/${brandBundleName}/assets/sprite.svg`)} data-revision={0} />
    </>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  pageType: PropTypes.string,
  firstContentElement: PropTypes.object,
  locale: PropTypes.string,
}

export default Layout
